/* eslint-disable no-bitwise */
angular.module('tgaApp').service('userService', [
  '$rootScope',
  '$http',
  '$location',
  '$window',
  'authService',
  'localStorageService',
  'envService',
  'domainService',
  'gameService',
  '$log',
  'scormService',
  function (
    $rootScope,
    $http,
    $location,
    $window,
    authService,
    localStorageService,
    envService,
    domainService,
    gameService,
    $log,
    scormService,
  ) {
    const serverAddress = envService.read('serverAddress');
    const storageKey = 'player';
    let user = null;

    function _uuid() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    }

    let session_limit_reached = false;
    /**
     *
     * @returns {boolean} to let game know that the user has reached their session limit.
     */
    function checkSessionLimitReached() {
      return session_limit_reached;
    }

    function isLoggedIn() {
      return authService.getAuthentication().isAuth;
    }

    function getUser() {
      const game_id = gameService.getGameId();
      return $http({
        method: 'GET',
        url: `${serverAddress}player?game_id=${game_id}`,
      }).then(
        (response) => response.data,
        (error) => {
          throw error.data;
        },
      );
    }

    function getUserData() {
      if (!user) {
        user = localStorageService.get(storageKey);
      }

      return user || {};
    }

    function setUser(newUser) {
      user = newUser;
      localStorageService.set(storageKey, user);
    }

    function updateUser(data) {
      return $http({
        method: 'PUT',
        url: `${serverAddress}player`,
        data,
      }).then(
        (response) => {
          setUser(response.data);
          return response.data;
        },
        (error) => {
          throw error.data;
        },
      );
    }

    function logout() {
      authService.clearAuthentication();
      setUser(null);
    }

    function login(data) {
      const authentication = authService.clearAuthentication();
      const sso = data.token;

      return $http({
        method: 'POST',
        url: `${serverAddress}player/login`,
        data: {
          username: data.username,
          password: data.password,
          token: data.token,
          game_id: data.game_id,
        },
      }).then(
        (response) => {
          authentication.isAuth = true;
          authentication.username = data.username;
          authentication.token = response.data.key;
          authentication.userId = response.data.id;

          authService.setAuthentication(authentication, true);

          return getUser().then((currentUser) => {
            // this is set to check whether the user is already logged in via SSO when
            // calling userService.register()
            if (sso) {
              currentUser.sso = true;
            }

            setUser(currentUser);
            return currentUser;
          });
        },
        (error) => {
          logout();
          throw error.data;
        },
      );
    }

    function clearScores(data) {
      return $http({
        method: 'POST',
        url: `${serverAddress}player/clear_scores`,
        data,
      }).then(
        (response) => {
          $log.debug('scores cleared');
          return response;
        },
        (error) => {
          $log.debug('error clearing scores');
          throw error.data;
        },
      );
    }

    // function to clear the scores for a SCORM player if they are restarting the game in the LMS
    function clearSCORMScoresOrNot() {
      if (scormService.isConnected() && scormService.provideEntryStatus() === 'new') {
        $log.debug('clearSCORMScoresOrNot: clearing prior scores for SCORM');
        return clearScores({ slug: $rootScope.slug });
      }
      return new Promise((res) => {
        res();
      });
    }

    function register(data) {
      data = data || {};
      data.game_id = gameService.getGameId();

      const authentication = authService.clearAuthentication();
      const u = getUserData();

      if (u.sso) {
        // If SSO then we need to keep the logged in user info
        // but they can still have a registration form where they fill in other stuff
        data = _.extend({}, _.omitBy(u, _.isNil), data);
        // don't overwrite stored username/email
        data.username = u.username;
        data.email = u.email;
      } else if (!data.username) {
        data.username = _uuid();
      }

      return $http({
        method: 'POST',
        url: `${serverAddress}player/register`,
        data,
      }).then(
        (response) => {
          authentication.isAuth = true;
          authentication.username = data.username;
          authentication.token = response.data.key;
          authentication.userId = response.data.id;
          authService.setAuthentication(authentication, true);

          return getUser().then((currentUser) => {
            setUser(currentUser);
            return clearSCORMScoresOrNot().then(() => currentUser);
          });
        },
        (error) => {
          throw error.data;
        },
      );
    }

    function checkAuthorizedUser(data) {
      data.domain = domainService.domain;
      return $http({
        method: 'POST',
        url: `${serverAddress}player/auth`,
        data,
      }).then(
        (response) => response.data.authorization,
        (error) => {
          throw error.data;
        },
      );
    }

    function startSession(data) {
      data = data || {};
      data.hub_id = $location.search().hub_id;
      data.course_id = $location.search().course_id;
      data.session_group_id = $location.search().session_group_id;
      data.challenge_id = $location.search().challenge_id;
      data.team_id = $location.search().team_id;
      data.is_scorm = scormService.isConnected();

      return $http({
        method: 'POST',
        url: `${serverAddress}player/session`,
        data,
      }).then(
        (response) => {
          if (data.hub_id) {
            $window.parent.postMessage({ session_id: response.data.session_id }, '*');
          }
          session_limit_reached = response.data.session_limit_reached ?? false;
          $log.debug('session started');
          return response.data;
        },
        (error) => {
          throw error.data;
        },
      );
    }

    return {
      checkSessionLimitReached,
      isLoggedIn,
      getUser,
      getUserData,
      setUser,
      updateUser,
      login,
      logout,
      register,
      startSession,
      checkAuthorizedUser,
      clearScores,
    };
  },
]);
